import { useState } from 'react';
import { LuHeadset, LuX } from 'react-icons/lu';
import { AnimatePresence } from 'framer-motion';
import { useEntitlement } from '../../hooks/useEntitlement';
import { useSupportChatStore } from '../../stores/support-chat.store';
import { useOnboardingStore } from '../../stores/onboarding.store';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { launcherClasses } from '../../design-system/launcher';
import SubscriptionModal from '../SubscriptionModal';
import SupportChatPanel from './SupportChatPanel';

interface SupportChatButtonProps {
  /**
   * 'floating' (default): fixed at the bottom right, as on desktop.
   * 'docked': in the flow of the layout's compact header below 1280
   * (redesign PR3: floating at right 16px it covered the right end of every
   * card on a phone).
   */
  position?: 'floating' | 'docked';
}

export default function SupportChatButton({ position = 'floating' }: SupportChatButtonProps = {}) {
  const isDesktop = useIsDesktop();
  const { isOpen, toggleOpen } = useSupportChatStore();
  const { showUpgrade } = useEntitlement('support_chat');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const quickTourPhase = useOnboardingStore((s) => s.quickTourPhase);
  const getCurrentQuickTourSection = useOnboardingStore((s) => s.getCurrentQuickTourSection);
  const isTourIntro = currentStep === 'quick-tour' && quickTourPhase === 'intro';
  const isSupportTourTarget = isTourIntro && getCurrentQuickTourSection() === 'support-chat';
  const isTourDimmed = isTourIntro && !isSupportTourTarget;

  const handleClick = () => {
    // Handle onboarding tour nav click
    const { currentStep, quickTourPhase } = useOnboardingStore.getState();
    if (currentStep === 'quick-tour' && quickTourPhase === 'intro') {
      useOnboardingStore.getState().handleTourNavClick('support-chat');
      return;
    }

    if (showUpgrade) {
      setShowSubscriptionModal(true);
      return;
    }
    toggleOpen();
  };

  // Floating: fixed at the bottom right. Docked: in the header's flow.
  const placement = launcherClasses({
    docked: position === 'docked',
    raised: isSupportTourTarget,
    dimmed: isTourDimmed,
    floatClass: isDesktop ? 'bottom-4 right-4' : 'bottom-24 right-4',
  });

  return (
    <>
      {/* Chat Panel */}
      <AnimatePresence>{isOpen && !showUpgrade ? <SupportChatPanel /> : null}</AnimatePresence>

      {/* Floating Button */}
      <button
        type="button"
        onClick={handleClick}
        data-onboarding="support-chat"
        className={`${placement} ${isOpen ? 'ee-launcher--open' : ''}`}
        aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
      >
        {isOpen ? <LuX aria-hidden="true" /> : <LuHeadset aria-hidden="true" />}
      </button>

      {/* Upgrade Modal for free users */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </>
  );
}
